import { FormSections, FormTabs } from "@/types/pages/form";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { QuoteContext } from "./QuoteContext";
import { RecommendProductsRequest } from "@/types/api/product";
import { recommendProducts } from "@/services/product";
import { showErrorNotification } from "@/utils/notifications/showErrorNotification";
import {
  clearSessionValues,
  readSessionValue,
  writeSessionValue,
} from "@/utils/formSessionStorage";

interface FormContextData {
  tab: FormTabs;
  formSection: FormSections;
  formAnswers: RecommendProductsRequest;
  submitForm: (allAnswers: RecommendProductsRequest) => Promise<void>;
  nextFormSection: (answers: any) => Promise<void>;
  prevFormSection: () => void;
  resetForm: () => void;
}

export const FormContext = createContext<FormContextData>(
  {} as FormContextData,
);

export const FormProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { setRecommendedProductsIds, setQuoteId, setQuoteItems } =
    useContext(QuoteContext);

  const [tab, setTab] = useState<FormTabs>(() => {
    const stored = readSessionValue<FormTabs>("tab", "questions");
    return stored === "order" ? "plan" : stored;
  });
  const [formSection, setFormSection] = useState<FormSections>(() => {
    const stored = readSessionValue<FormSections>("formSection", "about-you");
    return stored === "order" ? "recommendation" : stored;
  });

  const [formAnswers, setFormAnswers] = useState<RecommendProductsRequest>(
    () => readSessionValue("formAnswers", {} as any),
  );

  // Um F5 no meio do passo "order" cai de volta em "recommendation" (acima),
  // mas o quoteId/quoteItems/recommendedProductsIds persistidos ainda são os
  // da tentativa interrompida. Reaproveitar esse estado gera um novo quote
  // (saveQuote sempre chama createQuote) só que ainda dependente de contexto
  // de sessão obsoleto — na prática o checkout falha e só "Refazer
  // formulário" (resetForm) resolvia, porque só ele limpava essas chaves.
  // Aqui forçamos a mesma limpeza automaticamente quando detectamos o resume.
  const wasResumingOrderRef = useRef(
    readSessionValue<FormSections>("formSection", "about-you") === "order",
  );

  useEffect(() => {
    if (!wasResumingOrderRef.current) return;
    wasResumingOrderRef.current = false;

    // recommendedProductsIds fica: é só o resultado do quiz, não tem estado
    // de backend associado. quoteItems é recarregado automaticamente pelo
    // useQuoteResult a partir de recommendedProductsIds assim que a página
    // de recomendação monta — só precisa mesmo zerar aqui pra não sobrar
    // lixo entre o clear e esse refetch.
    setQuoteItems([]);
    setQuoteId(0);
    clearSessionValues(["quoteId", "quoteItems"]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    writeSessionValue("tab", tab);
  }, [tab]);

  useEffect(() => {
    writeSessionValue("formSection", formSection);
  }, [formSection]);

  useEffect(() => {
    writeSessionValue("formAnswers", formAnswers);
  }, [formAnswers]);

  useEffect(() => {
    const hasProgress = formSection !== "about-you" || Object.keys(formAnswers).length > 0;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!hasProgress) return;
      e.preventDefault();
      e.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [formSection, formAnswers]);

  const submitForm = async (allAnswers: RecommendProductsRequest) => {
    try {
      const res = await recommendProducts(allAnswers);

      const productsIds = res.map((product) => product.productId);

      setRecommendedProductsIds(productsIds);

      setTab("plan");
      setFormSection("recommendation");
    } catch (err: any) {
      showErrorNotification("Erro ao recomendar produtos: " + err.message);
    }
  };

  const prevFormSection = () => {
    if (formSection === "hair-loss") {
      setFormSection("about-you");
    }

    if (formSection === "health-history") {
      setFormSection("hair-loss");
    }

    if (formSection === "order") {
      setTab("plan");
      setFormSection("recommendation");
    }
  };

  const nextFormSection = useCallback(
    async (answers: any) => {
      if (formSection === "about-you") {
        setFormAnswers({
          ...formAnswers,
          ...answers,
        });

        setFormSection("hair-loss");
      }

      if (formSection === "hair-loss") {
        setFormAnswers({
          ...formAnswers,
          ...answers,
        });

        setFormSection("health-history");
      }

      if (formSection === "health-history") {
        setFormAnswers({
          ...formAnswers,
          ...answers,
        });

        await submitForm({
          ...formAnswers,
          ...answers,
        });
      }

      if (formSection === "recommendation") {
        setTab("order");
        setFormSection("order");
      }
    },
    [formSection, formAnswers, submitForm],
  );

  const resetForm = () => {
    setTab("questions");
    setFormSection("about-you");
    setRecommendedProductsIds([]);
    setQuoteItems([]);
    setQuoteId(0);
    setFormAnswers({} as any);
    clearSessionValues([
      "tab",
      "formSection",
      "formAnswers",
      "planDurationDays",
      "recommendedProductsIds",
      "quoteId",
      "quoteItems",
    ]);
  };

  return (
    <FormContext.Provider
      value={{
        tab,
        formSection,
        formAnswers,
        submitForm,
        nextFormSection,
        prevFormSection,
        resetForm,
      }}
    >
      {children}
    </FormContext.Provider>
  );
};
